import type { WatchedSecondsData, WatchedSecondsSlideData } from "../../../types/telemetry";
import { playerWatched } from "../../apollo/typePolicies/LocalStorage";
import type { Telemetry } from "../../telemetry/TelemetryModel";

/**
 * Helper function to update player watched data with telemetry payload
 */
export const updatePlayerWatchedPayload = (
  currentData: WatchedSecondsData,
  slideUuid: string,
  payload: Telemetry | null,
  updatedSeconds: number[],
): void => {
  if (payload) {
    const updatedData = {
      ...currentData,
      [slideUuid]: {
        payload, // Always update with latest payload
        seconds: updatedSeconds,
      },
    };
    playerWatched(updatedData);
  } else {
    console.error("Failed to create telemetry payload for slide tracking", {
      slideUuid,
      second: updatedSeconds[updatedSeconds.length - 1],
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Helper function to update stored media time in local storage
 */
export const setStoredMediaTime = (
  currentData: WatchedSecondsData,
  slideUuid: string,
  existingSlideData: WatchedSecondsSlideData,
  playerRef: React.RefObject<HTMLVideoElement>,
): void => {
  // Get current media_time from video
  const video = playerRef.current;
  const currentMediaTime = video?.currentTime ? Math.floor(video.currentTime) : 0;

  // Update payload with current media_time but keep everything else the same
  const updatedPayload = {
    ...existingSlideData.payload,
    media_time: currentMediaTime,
  };

  const updatedData = {
    ...currentData,
    [slideUuid]: {
      ...existingSlideData,
      payload: updatedPayload, // Update payload with current media_time
    },
  };

  playerWatched(updatedData);
};

export const formatTimestamp = (seconds: number): string => {
  const setValue = (value: number) => value.toString().padStart(2, "0");
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  return `${setValue(hours)}:${setValue(minutes)}:${setValue(secs)}`;
};

export const updateStoredMediaTime = (slideUuid: string, playerRef: React.RefObject<HTMLVideoElement>): void => {
  const currentData = playerWatched();
  const existingSlideData = currentData[slideUuid];

  if (existingSlideData) {
    setStoredMediaTime(currentData, slideUuid, existingSlideData, playerRef);
  }
};

export const executeWhenPlayerReady = (
  playerRef: React.RefObject<HTMLVideoElement>,
  onPlayerReady: (currentSecond: number) => void,
): number | undefined => {
  if (playerRef.current?.duration && isFinite(playerRef.current.duration)) {
    const currentSecond = Math.floor(playerRef.current.currentTime);
    onPlayerReady(currentSecond);
    return currentSecond;
  }
  return undefined;
};
